import type { Locale } from "@commerce-frontend/i18n/types";
import type { Store, StoreOpeningHours } from "@commerce-frontend/types";
import { Tag } from "@commerce-frontend/ui/base/Tag";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { isValue } from "@labdigital/toolkit";
import { useTranslations } from "next-intl";
import type { Day } from "../../_lib/date-time";
import { days, formatTime } from "../../_lib/date-time";

type Props = {
	store: Store;
	locale: Locale;
};

type OpeningTimesArray = [string, string] | [string, string, string, string] | null;
type OpeningHoursState = "pause" | "no-pause" | "closed";

type OpeningTimesResult = {
	openingTimes: OpeningTimesArray;
	state: OpeningHoursState;
};

/**
 * Converts the openingHours object into an array of opening times for the specified day
 * and returns the state of the opening hours
 *
 * @param {Day} day
 * Example: monday
 * @param {Object} openingHours
 * Example: {"mondayopen1":540,"mondayclose1":1050,"mondayopen2":null,"mondayclose2":null}
 */
export const getOpeningTimes = (
	locale: Locale,
	day: Day,
	openingHours: StoreOpeningHours,
): OpeningTimesResult => {
	/** Example: [540, 1050, null, null] */
	const dayOpeningTimes = ["open1", "close1", "open2", "close2"].map(
		(suffix) => openingHours?.[(day + suffix) as keyof StoreOpeningHours],
	);

	/** Example: ['09:00', '17:30'] */
	const openingTimes = dayOpeningTimes
		.map((time) => (time ? formatTime(time, locale) : null))
		.filter(isValue) as OpeningTimesArray;

	let state: OpeningHoursState;

	if (openingTimes?.length === 2) {
		state = "no-pause";
	} else if (openingTimes?.length === 4) {
		state = "pause";
	} else {
		state = "closed";
		return { openingTimes: null, state };
	}

	return { openingTimes, state };
};

export const OpeningHours = ({ store, locale }: Props) => {
	const t = useTranslations("StoreInfo");
	const today: Day = days[new Date().getDay()];

	return (
		<div>
			{days.map((day) => {
				const { openingTimes, state } = getOpeningTimes(locale, day, store?.openingHours || {});

				return (
					<div key={day} className={cn("flex", day === today && "font-bold")}>
						<div className="w-28 shrink-0 grow-0 text-gray-700">{t(day)}:</div>
						<div className="text-gray-700">
							{t(`opening-times-${state}`, {
								open: openingTimes?.[0],
								close: openingTimes?.[1],
								open2: openingTimes?.[2],
								close2: openingTimes?.[3],
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};

/**
 * Tag component with opening hours for the current day
 */
export const OpeningHoursToday = ({ store, locale }: Props) => {
	const t = useTranslations("StoreInfo");
	const today: Day = days[new Date().getDay()];
	const { openingTimes, state } = getOpeningTimes(locale, today, store?.openingHours || {});

	return (
		<Tag type={state === "closed" ? "error" : "success"}>
			{t(`todays-opening-times-${state}`, {
				open: openingTimes?.[0],
				close: openingTimes?.[1],
				open2: openingTimes?.[2],
				close2: openingTimes?.[3],
			})}
		</Tag>
	);
};
